import { Button, Card, Col, Divider, Flex, Form, FormProps, Row, Tooltip } from 'antd';
import { FunctionComponent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

// components
import PageTitle from 'components/title/title';
import ComponentHeader from 'components/componentHeader/componentHeader';
import SwitchElement from 'components/switch/switch';
import FormNumberElement from 'components/formNumberElement/formNumberElement';
import LoadingComponent from 'components/loadingComponent/loadingComponent';
import InfoTooltip from 'components/info/info';
import ConfirmModalComponent from 'components/confirmModal/confirmModal.component';

// services
import { onGetTransferSettings, onUpdateActivationTransferSettings, onUpdateLimitationTransferSettings } from '../services/transactionsService';
import NotificationService from 'core/services/notification';
import { ActivationTransferSettings, LimitsTransferSettings, TransferSettings } from '../models/transaction';
import { isRTL } from 'core/services/language';
import useCooperative from 'core/hooks/useCooperative';
import { mapResponseErrors } from 'utils/mapResponseErrors';
import { onCheckGeneralSuspend } from 'modules/main/actions/suspend/services/suspendServices';

// icons
import { InfoCircleTwoTone } from '@ant-design/icons';

const SettingsComponent: FunctionComponent = () => {
	const { t } = useTranslation();
	const { isCooperative } = useCooperative();

	const [limitationForm] = Form.useForm();
	const [activationForm] = Form.useForm();

	const [isLoading, setIsLoading] = useState(false);
	const [isSubmittingLimitationSettings, setIsSubmittingLimitationSettings] = useState(false);
	const [isSubmittingActivationSettings, setIsSubmittingActivationSettings] = useState(false);

	const [hasActiveGeneralSuspend, setHasActiveGeneralSuspend] = useState(false);
	const [haltTransfer, setHaltTransfer] = useState(false);
	const [companyApprovalValue, setCompanyApprovalValue] = useState(false);

	const [limitationModalOpen, setLimitationModalOpen] = useState(false);
	const [activationModalOpen, setActivationModalOpen] = useState(false);

	useEffect(() => {
		getTransferSettings();
		checkGeneralSuspend();
	}, []);

	const getTransferSettings = async () => {
		try {
			setIsLoading(true);
			const response = await onGetTransferSettings();

			limitationForm.setFieldsValue({
				minimum_shares_per_transfer: response.minimum_shares_per_transfer,
				maximum_shares_per_transfer: response.maximum_shares_per_transfer,
				minimum_transfer_selling_price: Number(response.minimum_transfer_selling_price),
			});
			activationForm.setFieldsValue({
				halt_transfer: response.halt_transfer,
				company_approval_on_transfer_requests: response.company_approval_on_transfer_requests,
				view_last_price_chart: response.view_last_price_chart,
			});
			setHaltTransfer(response.halt_transfer!);
			setCompanyApprovalValue(response.company_approval_on_transfer_requests!);
			setIsLoading(false);
		} catch (error) {
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
		}
	};

	const checkGeneralSuspend = async () => {
		try {
			const hasActiveGeneralSuspend = await onCheckGeneralSuspend();
			setHasActiveGeneralSuspend(Boolean(hasActiveGeneralSuspend));
		} catch (error) {
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
		}
	};

	const submitLimitationForm = async (values: LimitsTransferSettings) => {
		try {
			setIsSubmittingLimitationSettings(true);
			await onUpdateLimitationTransferSettings(values);
			setIsSubmittingLimitationSettings(false);
			NotificationService.success(t('COMMON.SUCCESS'), t('COMPANY_INFORMATION.LIMITATION_TRANSFER_SETTINGS_UPDATED_SUCCESSFULLY'));
		} catch (error) {
			setIsSubmittingLimitationSettings(false);
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.SUBMIT_ERROR'));
			mapResponseErrors(error, limitationForm);
		}
	};

	const submitActivationForm = async (values: ActivationTransferSettings) => {
		try {
			setIsSubmittingActivationSettings(true);

			const payload: ActivationTransferSettings = {
				view_last_price_chart: values.view_last_price_chart,
			};

			if (!hasActiveGeneralSuspend) {
				payload.halt_transfer = values.halt_transfer;
				payload.company_approval_on_transfer_requests = values.company_approval_on_transfer_requests;
			}

			if (isCooperative) {
				payload.company_approval_on_transfer_requests = true;
				payload.view_last_price_chart = false;
			}

			await onUpdateActivationTransferSettings(payload);
			setIsSubmittingActivationSettings(false);
			NotificationService.success(t('COMMON.SUCCESS'), t('COMPANY_INFORMATION.ACTIVATION_TRANSFER_SETTINGS_UPDATED_SUCCESSFULLY'));
		} catch (error) {
			setIsSubmittingActivationSettings(false);
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.SUBMIT_ERROR'));
			mapResponseErrors(error, activationForm);
		}
	};

	const onConfirmLimitation = async () => {
		await submitLimitationForm(limitationForm.getFieldsValue());
		setLimitationModalOpen(false);
	};

	const onConfirmActivation = async () => {
		await submitActivationForm(activationForm.getFieldsValue());
		setActivationModalOpen(false);
	};

	return (
		<>
			<PageTitle name={t('COMPANY_INFORMATION.SETTINGS')} />

			<ConfirmModalComponent
				open={limitationModalOpen}
				title={t('COMPANY_INFORMATION.SETTINGS_CONFIRM_TITLE')}
				confirmText={t('COMMON.CONFIRM')}
				confirmColor="danger"
				onDiscard={() => setLimitationModalOpen(false)}
				onConfirm={onConfirmLimitation}
				isLoading={isSubmittingLimitationSettings}
				note={<p className="mb-0">{t('COMPANY_INFORMATION.TRANSFER_RESTRICTION_NOTE')}</p>}
				noteType="red"
				large
			/>

			<ConfirmModalComponent
				open={activationModalOpen}
				title={t('COMPANY_INFORMATION.SETTINGS_CONFIRM_TITLE')}
				confirmText={t('COMMON.CONFIRM')}
				confirmColor="danger"
				onDiscard={() => setActivationModalOpen(false)}
				onConfirm={onConfirmActivation}
				isLoading={isSubmittingActivationSettings}
				note={<p className="mb-0">{t('COMPANY_INFORMATION.HALT_TRANSFER_NOTE')}</p>}
				noteType="red"
				large
			/>

			<Card className="mt-4">
				<ComponentHeader title={t('COMPANY_INFORMATION.TRANSFER_SETTINGS')} subtitle={t('COMPANY_INFORMATION.CUSTOMIZE_TRANSFER_RULES')} />

				<Divider />

				{isLoading ? (
					<LoadingComponent />
				) : (
					<Flex wrap>
						<div style={{ flex: 1 }}>
							<Form form={limitationForm} onFinish={() => setLimitationModalOpen(true)}>
								<div style={{ flex: 1 }}>
									<h6 className="text-primary fw-medium mb-3">{t('COMPANY_INFORMATION.TRANSFER_LIMITATION')}</h6>
									<main style={{ minHeight: 160 }}>
										<Flex align="center" gap={10} className="mb-3">
											<p className="mb-0" style={{ minWidth: isRTL() ? 180 : 220 }}>
												{t('COMPANY_INFORMATION.MINIMUM_SHARES_PER_TRANSFER')}
											</p>
											<Flex gap={5} align="center">
												<InfoTooltip title={t('COMPANY_INFORMATION.EMPTY_INFORMATION_TOOLTIP')} />
												<FormNumberElement
													name="minimum_shares_per_transfer"
													inputStyles={{ width: '180px' }}
													formClasses="mb-0"
													inputClasses="text-center"
													size="middle"
													rules={[{ type: 'number', min: 1, message: t('COMPANY_INFORMATION.MINIMUM_SHARES_PER_TRANSFER_MIN') }]}
													min={1}
													addonAfter={<span>{t('COMMON.SHARES')}</span>}
												/>
											</Flex>
										</Flex>
										<Flex align="center" gap={10} className="mb-3">
											<p className="mb-0" style={{ minWidth: isRTL() ? 180 : 220 }}>
												{t('COMPANY_INFORMATION.MAXIMUM_SHARES_PER_TRANSFER')}
											</p>
											<Flex gap={5} align="center">
												<InfoTooltip title={t('COMPANY_INFORMATION.EMPTY_INFORMATION_TOOLTIP')} />
												<FormNumberElement
													name="maximum_shares_per_transfer"
													inputStyles={{ width: '180px' }}
													formClasses="mb-0"
													inputClasses="text-center"
													size="middle"
													rules={[{ type: 'number', min: 1, message: t('COMPANY_INFORMATION.MAXIMUM_SHARES_PER_TRANSFER_MIN') }]}
													min={1}
													addonAfter={<span>{t('COMMON.SHARES')}</span>}
												/>
											</Flex>
										</Flex>
										{!isCooperative && (
											<Flex align="center" gap={10} className="mb-3">
												<p className="mb-0" style={{ minWidth: isRTL() ? 180 : 220 }}>
													{t('COMPANY_INFORMATION.MINIMUM_TRANSFER_SELLING_PRICE')}
												</p>
												<Flex gap={5} align="center">
													<InfoTooltip title={t('COMPANY_INFORMATION.EMPTY_INFORMATION_TOOLTIP')} />
													<FormNumberElement
														name="minimum_transfer_selling_price"
														inputStyles={{ width: '180px' }}
														formClasses="mb-0"
														inputClasses="text-center"
														size="middle"
														min={0.01}
														precision={2}
														addonAfter={<span>{t('COMPANY_INFORMATION.PER_SHARE')}</span>}
														rules={[{ type: 'number', min: 0.01, message: t('COMPANY_INFORMATION.MINIMUM_TRANSFER_SELLING_PRICE_MIN') }]}
													/>
												</Flex>
											</Flex>
										)}
									</main>
								</div>

								<Flex justify="end" className="mt-3">
									<Button htmlType="submit" type="primary">
										{t('COMMON.UPDATE')}
									</Button>
								</Flex>
							</Form>
						</div>

						<div>
							<div style={{ width: 1, height: '100%', backgroundColor: '#E4E7EC', margin: '0px 20px' }}></div>
						</div>

						<div style={{ flex: 1 }}>
							<Form form={activationForm} onFinish={() => setActivationModalOpen(true)}>
								<div style={{ flex: 1, minWidth: 350 }}>
									<h6 className="text-primary fw-medium mb-4">{t('COMPANY_INFORMATION.TRANSFER_ACTIVATIONS')}</h6>
									<main style={{ minHeight: 160 }}>
										<Flex align="center" gap={10} className="mb-4">
											<SwitchElement
												label={t('COMPANY_INFORMATION.HALT_TRANSFER')}
												onChange={(value) => {
													activationForm.setFieldValue('halt_transfer', value);
													setHaltTransfer(value);
												}}
												checked={activationForm.getFieldValue('halt_transfer')}
												disabled={hasActiveGeneralSuspend}
												className="mb-0"
											/>
											<Form.Item name="halt_transfer" hidden noStyle />

											{hasActiveGeneralSuspend && (
												<InfoTooltip
													color="red"
													customIcon={<InfoCircleTwoTone twoToneColor="#c54141" />}
													title={t('COMPANY_INFORMATION.GENERAL_SUSPEND_MESSAGE')}
												/>
											)}
										</Flex>

										{!isCooperative && (
											<Flex align="center" gap={10} className="mb-4">
												<SwitchElement
													key={String(haltTransfer)}
													label={t('COMPANY_INFORMATION.COMPANY_APPROVAL_ON_TRANSFER_REQUESTS')}
													checked={haltTransfer ? false : companyApprovalValue}
													disabled={haltTransfer || hasActiveGeneralSuspend}
													className="mb-0"
													onChange={(value) => {
														activationForm.setFieldValue('company_approval_on_transfer_requests', value);
														setCompanyApprovalValue(value);
													}}
												/>
												<Form.Item name="company_approval_on_transfer_requests" hidden noStyle />

												{hasActiveGeneralSuspend && (
													<InfoTooltip
														color="red"
														customIcon={<InfoCircleTwoTone twoToneColor="#c54141" />}
														title={t('COMPANY_INFORMATION.GENERAL_SUSPEND_MESSAGE')}
													/>
												)}
											</Flex>
										)}

										{!isCooperative && (
											<Flex align="center" gap={10}>
												<SwitchElement
													label={t('COMPANY_INFORMATION.VIEW_LAST_PRICE_CHART')}
													checked={activationForm.getFieldValue('view_last_price_chart')}
													onChange={(value) => activationForm.setFieldValue('view_last_price_chart', value)}
												/>
												<Form.Item name="view_last_price_chart" hidden noStyle />
											</Flex>
										)}
									</main>
								</div>

								<Flex justify="end" className="mt-3">
									<Button htmlType="submit" type="primary" size="large">
										{t('COMMON.UPDATE')}
									</Button>
								</Flex>
							</Form>
						</div>
					</Flex>
				)}
			</Card>
		</>
	);
};

export default SettingsComponent;
